import React, { useEffect, useState } from "react";
import CommonSlider from "../../../common/CommonSlider";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { isValidExpiryDate, formatYYYYMMDD, toDate } from "../../../../helpers/input-validations";
import CustomInput from "../../../common/CustomInput";
import Button from "../../../common/Button";
import {
  insurancePlanOptions,
  orderOfInsuranceOptions,
  thirdPartyInsuranceStatusOptions,
} from "../../create/options";
import { useDispatch } from "react-redux";
import {
  createPatientPrivateInsuranceDetails,
  updatePatientPrivateInsuranceDetails,
} from "../../../../redux/modules/patient/actions";

const ThirdPartyCreate = ({
  open,
  onClose,
  insurance,
  patient,
  create = false,
}) => {
  const dispatch = useDispatch();
  const buildInitialPlan = () => ({
    id: create ? "" : insurance.id,
    insurancePlanName: create ? "" : insurance.insurancePlanName,
    otherInsurancePlanName: create ? "" : insurance.otherInsurancePlanName,
    orderOfInsurance: create ? "" : insurance.orderOfInsurance,
    insuranceExpiryDate: create ? "" : insurance.expiryDate,
    insuranceEffectiveDate: create ? "" : insurance.effectiveDate,
    comments: create ? "" : insurance.comments,
    status: create ? "" : insurance.status,
    errors: {
      insurancePlanName: false,
      orderOfInsurance: false,
      insuranceExpiryDate: false,
      status: false,
    },
  });

  const [insurancePlans, setInsurancePlans] = useState([buildInitialPlan()]);

  useEffect(() => {
    if (!open) return;
    setInsurancePlans([buildInitialPlan()]);
  }, [open, create, insurance]);

  const validateField = (field, value) => {
    let errorMessage = false;
    
    // Handle null or undefined values - convert to empty string
    const stringValue = value == null ? "" : String(value);

    if (field === "insurancePlanName") {
      // Perform validation for insurance plan name
      // Example validation:
      if (stringValue.trim() === "") {
        errorMessage = true;
      }
    } else if (field === "otherInsurancePlanName") {
      // Perform validation for insurance plan name
      // Example validation:
      if (stringValue.trim() === "") {
        errorMessage = true;
      }
    } else if (field === "orderOfInsurance") {
      // Optional: only validate if user provided a value
      if (stringValue.trim() !== "") {
        errorMessage = false;
      }
    } else if (
      field === "insuranceExpiryDate" ||
      field === "insuranceEffectiveDate"
    ) {
      // Perform validation for insurance expiry date
      // Example validation:
      if (stringValue.trim() === "" || !isValidExpiryDate(stringValue)) {
        errorMessage = true;
      }
    } else if (field === "status") {
      // Perform validation for status
      // Example validation:
      if (stringValue.trim() === "") {
        errorMessage = true;
      }
    }

    return errorMessage;
  };

  const updateErrors = (index, key, value) => {
    const updatedPlans = [...insurancePlans];
    const errorMessage = value === "" ? false : validateField(key, value);
    updatedPlans[index].errors[key] = errorMessage;
    setInsurancePlans(updatedPlans);
  };

  const normalizeValue = (key, value) => {
    if (value == null) return "";
    if (typeof value === "object" && value?.value !== undefined) return value.value;
    if (key !== "orderOfInsurance" && value === "none") return "";
    return value;
  };

  const handleInputChange = (index, key, value) => {
    const normalizedValue = normalizeValue(key, value);
    const updatedPlans = [...insurancePlans];
    updatedPlans[index][key] = normalizedValue;

    if (key === "insurancePlanName") {
      if (normalizedValue === "") {
        updatedPlans[index].status = "";
      } else if (!updatedPlans[index].status) {
        updatedPlans[index].status = thirdPartyInsuranceStatusOptions[0]?.value || "active";
      }
    }

    updateErrors(index, key, normalizedValue);

    const isCompleted = Object.values(updatedPlans[index].errors).every(
      (error) => error === false
    );
    updatedPlans[index].isCompleted = isCompleted;

    setInsurancePlans(updatedPlans);
  };

  const manageStep = () => {
    let isInsuranceValidated = true;

    // Only enforce required validation when creating a new record
    if (create) {
      insurancePlans?.forEach((plan) => {
        if (plan.insurancePlanName === "") {
          isInsuranceValidated = false;
          updateErrors(0, "insurancePlanName", plan.insurancePlanName);
          return;
        }

        if (
          plan.insurancePlanName === "other" &&
          validateField("otherInsurancePlanName", plan.otherInsurancePlanName)
        ) {
          isInsuranceValidated = false;
        }

        if (validateField("status", plan.status)) {
          isInsuranceValidated = false;
          updateErrors(0, "status", plan.status);
        }
      });
    }

    if (isInsuranceValidated) {
      const plan = insurancePlans?.[0] ?? {};

      // Only format dates if they were actually entered (not empty or null)
      // Check if the date value exists and is not an empty string
      const effectiveDateStr = plan.insuranceEffectiveDate && 
        String(plan.insuranceEffectiveDate).trim() !== ""
        ? formatYYYYMMDD(toDate(plan.insuranceEffectiveDate))
        : null;

      const expiryDateStr = plan.insuranceExpiryDate && 
        String(plan.insuranceExpiryDate).trim() !== ""
        ? formatYYYYMMDD(toDate(plan.insuranceExpiryDate))
        : null;

      if (!create) {
        const insuranceBody = {
          insurancePlanName: plan.insurancePlanName ?? null,
          otherInsurancePlanName: plan.otherInsurancePlanName ?? null,
          expiryDate: expiryDateStr,
          effectiveDate: effectiveDateStr,
          orderOfInsurance:
            plan.orderOfInsurance === "none" ? null : (plan.orderOfInsurance ?? null),
          status: plan.status, 
          id: plan.id, 
          comments: plan.comments ?? null,
        };

        dispatch(
          updatePatientPrivateInsuranceDetails(insuranceBody.id, insuranceBody)
        );
      } else {
        const getStatusValue = () => {
          const statusString = plan.status ? String(plan.status) : "";
          const isValidStatus = statusString === "active" || statusString === "inactive";
          return isValidStatus 
            ? statusString 
            : (statusString?.trim() || thirdPartyInsuranceStatusOptions[0].value);
        };

        const insuranceBody = {
          insurancePlanName: plan.insurancePlanName ?? null,
          otherInsurancePlanName: plan.otherInsurancePlanName ?? null,
          expiryDate: expiryDateStr,
          effectiveDate: effectiveDateStr,
          orderOfInsurance:
            plan.orderOfInsurance === "none" ? null : (plan.orderOfInsurance ?? null),
          status: getStatusValue(),
          comments: plan.comments ?? null,
        };

        dispatch(createPatientPrivateInsuranceDetails(patient.id, insuranceBody));
      }

      onClose(false);
    }
  };

  return (
    <CommonSlider  open={open} onClose={onClose}>
      <div className="flex flex-col flex-1 h-full overflow-y-auto ">
        <div className="relative flex flex-col flex-1">
          {/* Header */}
          <div className="px-8 py-6 bg-gray-20">
            <div className="flex items-start justify-between space-x-3">
              <div className="flex flex-col">
                <p className="font-bold text-gray-800 text-display-xs">
                  {create
                    ? "Add Third Party Insurance Information"
                    : "Edit Third Party Insurance Information"}
                </p>
                <p className="text-body-md text-gray-700">
                  {patient.lastName}, {patient.firstName}
                </p>
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-teal-600 hover:text-blue-200 text-teal-600"
                  onClick={() => onClose(false)}
                >
                  <span className="sr-only">Close panel</span>
                  <XMarkIcon
                    className="w-5 h-5 m-auto"
                    strokeWidth="2"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-col justify-between flex-1 px-8 mt-4">
            <div className="grid grid-cols-12 gap-3">
              <CustomInput
                options={insurancePlanOptions}
                name={`insurancePlanName_${0}`}
                className="col-span-12"
                label="Insurance Plan Name"
                type="dropdown"
                required={create}
                disabled={!create}
                value={
                  insurancePlans[0].insurancePlanName?.length > 0
                    ? insurancePlans[0].insurancePlanName
                    : ""
                }
                error={insurancePlans[0].errors.insurancePlanName}
                onChange={(e) =>
                  handleInputChange(0, "insurancePlanName", e.value)
                }
              />
              {insurancePlans[0].insurancePlanName === "other" && (
                <>
                  <CustomInput
                    name={`otherInsurancePlanName${0}`}
                    className="col-span-12"
                    label="Other (Insurance Plan Name)"
                    placeholder={"Enter Name"}
                    inputClassName="!placeholder-gray-600"
                    type="text"
                    required
                    value={insurancePlans[0]?.otherInsurancePlanName}
                    onChange={(e) =>
                      handleInputChange(0, "otherInsurancePlanName", e.value)
                    }
                  />
                </>
              )}
              <CustomInput
                options={orderOfInsuranceOptions}
                name={`orderOfInsurance_${0}`}
                className="col-span-12"
                label="Order of Insurance"
                type="dropdown"
                value={
                  insurancePlans[0].orderOfInsurance?.length > 0
                    ? insurancePlans[0].orderOfInsurance
                    : ""
                }
                error={insurancePlans[0].errors.orderOfInsurance}
                onChange={(e) =>
                  handleInputChange(0, "orderOfInsurance", e.value)
                }
              />
              <CustomInput
                name={`insuranceExpiryDate_${0}`}
                className="col-span-12"
                label="Expiry Date"
                type="date"
                value={insurancePlans[0].insuranceExpiryDate}
                error={insurancePlans[0].errors.insuranceExpiryDate}
                onChange={(e) => handleInputChange(0, "insuranceExpiryDate", e)}
              />
              <CustomInput
                name={`insuranceEffectiveDate_${0}`}
                className="col-span-12"
                label="Effective Date"
                type="date"
                value={insurancePlans[0].insuranceEffectiveDate}
                error={insurancePlans[0].errors.insuranceEffectiveDate}
                onChange={(e) =>
                  handleInputChange(0, "insuranceEffectiveDate", e)
                }
              />
              {(
                <CustomInput
                  options={thirdPartyInsuranceStatusOptions}
                  name={`status_${0}`}
                  className="col-span-12"
                  label="Status"
                  type="dropdown"
                  required
                  value={insurancePlans[0]?.status || ""}
                  error={insurancePlans[0]?.errors?.status}
                  onChange={(selectedValue) => handleInputChange(0, "status", selectedValue)}
                />
              )}
              <CustomInput
                name={`commentsInsurance_${0}`}
                className="col-span-12 my-4"
                label="Comments"
                placeholder="Enter Note"
                type="text"
                value={insurancePlans[0].comments}
                onChange={(e) =>
                  handleInputChange(0, "comments", e.target.value)
                }
              />
            </div>
            <div className="flex items-center justify-center py-6">
              <div className="flex items-center justify-center space-x-5">
                <Button
                  className="min-w-[200px]"
                  text={`Cancel`}
                  type="secondary"
                  onClick={() => onClose(false)}
                />
                <Button
                  className="min-w-[200px]"
                  text={`${create ? "Save" : "Update"}`}
                  type="primary"
                  onClick={() => manageStep()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </CommonSlider>
  );
};

export default ThirdPartyCreate;
